import { REHYDRATE } from "redux-persist";
import api from "./apiUtils";
import { setApiError } from "../../Store/reducers/APIReducer";
import { setAuthorisedUser, logout } from "../../Store/reducers/AppReducer";
import { sessionExpired } from "../constants";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../lib/firebase";
import { getFreshIdToken } from "../../lib/firebaseAuth";
import {
  clearStoredIdToken,
  getStoredIdToken,
  getStoredTokenExpiryMs,
  getTokenExpiryFromJwt,
} from "../../lib/tokenUtils";

let requestInterceptorId;
const EXPIRY_SAFETY_WINDOW_MS = 2 * 60 * 1000;

const waitForAuthReady = () =>
  new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user || null);
    });
  });

const ensureIdToken = async (forceRefresh) => {
  try {
    const user = auth.currentUser || (await waitForAuthReady());
    if (!user) {
      const stored = getStoredIdToken();
      const expiry = getTokenExpiryFromJwt(stored);
      if (stored && (!expiry || expiry > Date.now())) {
        return stored;
      }
      throw new Error("No authenticated user");
    }
    const token = await getFreshIdToken(forceRefresh);
    if (token) return token;
  } catch (error) {
    console.error(
      `Silent token refresh (${forceRefresh ? "force" : "cache"}) failed:`,
      error
    );
  }
  return null;
};

export const attachInterceptor = (companyUserData, dispatch) => {
  if (requestInterceptorId !== undefined) {
    api.interceptors.request.eject(requestInterceptorId);
  }

  requestInterceptorId = api.interceptors.request.use(async (config) => {
    try {
      let accessToken = getStoredIdToken();
      let expiryMs = getStoredTokenExpiryMs();

      if (accessToken && expiryMs === null) {
        const computedExpiry = getTokenExpiryFromJwt(accessToken);
        if (computedExpiry) {
          localStorage.setItem("idTokenExpiryMs", String(computedExpiry));
          expiryMs = computedExpiry;
        }
      }

      const now = Date.now();
      const shouldRefresh =
        !accessToken ||
        (expiryMs !== null && expiryMs - now <= EXPIRY_SAFETY_WINDOW_MS);

      if (shouldRefresh) {
        const force = expiryMs !== null && expiryMs <= now;
        const refreshed = await ensureIdToken(force);
        if (refreshed) {
          accessToken = refreshed;
        }
      }

      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      const normalizedUrl =
        typeof config?.url === "string" ? config.url.toLowerCase() : "";
      if (normalizedUrl.includes("upsertdetails")) {
        config.headers["Content-Type"] = "application/json; charset=utf-8";
      }
      const storedCompanyId = sessionStorage.getItem("selectedAdminCompanyId");
      const selectedCompanyId = storedCompanyId || companyUserData?.id;
      if (selectedCompanyId) {
        config.headers["x-company-id"] = selectedCompanyId;
      }
    } catch (error) {
      console.error("Error setting token in interceptor:", error);
    }
    return config;
  });

  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const accessToken = await ensureIdToken(true);
          if (!accessToken) {
            throw new Error("Token refresh failed");
          }
          api.defaults.headers.common[
            "Authorization"
          ] = `Bearer ${accessToken}`;
          originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
          return api(originalRequest);
        } catch (err) {
          if (requestInterceptorId !== undefined) {
            api.interceptors.request.eject(requestInterceptorId);
          }
          clearStoredIdToken();
          dispatch(setAuthorisedUser(null));
          dispatch(setApiError(sessionExpired));
          dispatch(logout());
          return Promise.reject(err);
        }
      }

      const shouldRetryUpsert =
        error.response?.status === 500 &&
        !originalRequest?._retry500 &&
        typeof originalRequest?.url === "string" &&
        originalRequest.url.toLowerCase().includes("upsertdetails");

      if (shouldRetryUpsert) {
        originalRequest._retry500 = true;
        return api(originalRequest);
      }

      return Promise.reject(error);
    }
  );
};

export const tokenMiddleware = (store) => (next) => (action) => {
  if (action.type === "App/setAuthorisedUser" || action.type === REHYDRATE) {
    const companyUserData =
      action.payload?.companyAdmin?.selectedAdminCompany ||
      store.getState().companyAdmin?.selectedAdminCompany;
    attachInterceptor(companyUserData, store.dispatch);
  }
  if (action.type === "LOGOUT") {
    if (requestInterceptorId !== undefined) {
      api.interceptors.request.eject(requestInterceptorId);
    }
    clearStoredIdToken();
  }
  return next(action);
};
